import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const STATUS_COLORS = {
  picked_up: "#d4a531",
  in_transit: "#2563eb",
  delivered: "#1b4332",
  onforwarded: "#ea580c",
};

const pinIcon = (color) => L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 22],
});

function fmt(ts) {
  try { return new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}

export default function BreadcrumbMap({ history, driverLocation }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const points = (history || []).filter((h) => h.location && h.location.lat != null && h.location.lng != null);
  const live = driverLocation && driverLocation.lat != null ? driverLocation : null;

  useEffect(() => {
    if (!ref.current || (points.length === 0 && !live)) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const map = L.map(ref.current, { scrollWheelZoom: false });
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    const latlngs = points.map((p) => [p.location.lat, p.location.lng]);
    if (latlngs.length > 1) {
      L.polyline(latlngs, { color: "#1b4332", weight: 3, opacity: 0.8, dashArray: "6 6" }).addTo(map);
    }
    points.forEach((p) => {
      const color = STATUS_COLORS[p.status] || "#6b7280";
      L.marker([p.location.lat, p.location.lng], { icon: pinIcon(color) })
        .addTo(map)
        .bindPopup(`<b style="text-transform:capitalize">${p.status.replace(/_/g, " ")}</b><br/>${fmt(p.timestamp)}${p.note ? `<br/><span style="color:#6b7280">${p.note}</span>` : ""}`);
    });
    if (live) {
      const liveIcon = L.divIcon({ className: "", html: '<div class="kc-live-pin"></div>', iconSize: [16, 16], iconAnchor: [8, 8] });
      L.marker([live.lat, live.lng], { icon: liveIcon, zIndexOffset: 1000 })
        .addTo(map)
        .bindPopup(`<b>Driver — live position</b><br/>Updated ${fmt(live.updated_at)}`);
      latlngs.push([live.lat, live.lng]);
    }
    if (latlngs.length === 1) map.setView(latlngs[0], 15);
    else map.fitBounds(L.latLngBounds(latlngs), { padding: [30, 30] });
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [JSON.stringify(points), JSON.stringify(live)]); // eslint-disable-line react-hooks/exhaustive-deps

  if (points.length === 0 && !live) return null;
  return (
    <div className="mb-6" data-testid="breadcrumb-map-section">
      <p className="mb-2 flex items-center justify-between text-xs font-semibold uppercase text-muted-foreground">
        <span>Recorded courier locations</span>
        {live && <span className="flex items-center gap-1.5 normal-case text-blue-600" data-testid="live-driver-indicator"><span className="inline-block h-2 w-2 animate-pulse rounded-full bg-blue-600" /> Driver live</span>}
      </p>
      <div ref={ref} data-testid="breadcrumb-map" className="h-64 w-full overflow-hidden rounded-lg border border-border sm:h-72" />
    </div>
  );
}
